import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useLanguage } from "../contexts/LanguageContext";
import Header from "../components/Header";
import Footer from "../components/Footer";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { ShoppingCart, Search, Filter, ExternalLink } from "lucide-react";

const ShopPage = () => {
  const { t } = useLanguage();
  const [selectedBrand, setSelectedBrand] = useState("all");
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  const brands = [
    {
      id: "exl",
      name: "EXL",
      logo: "https://customer-assets.emergentagent.com/job_excel-levage/artifacts/orfufguk_Logo%20EXL.svg",
      description: "Notre marque maison d'équipements de levage de qualité industrielle",
      available: true
    },
    {
      id: "vulcan",
      name: "VulcanHoist",
      logo: "https://customer-assets.emergentagent.com/job_excel-levage/artifacts/be0y48en_Vulcan.svg",
      description: "Solutions de levage professionnelles pour l'industrie lourde",
      available: true
    },
    {
      id: "gorbel",
      name: "Gorbel",
      logo: "https://customer-assets.emergentagent.com/job_excel-levage/artifacts/24b3eshx_gorbel_2024-logo.png",
      description: "Leader mondial en équipements de manutention ergonomiques",
      available: true
    }
  ];

  const categories = [
    { id: "hoists", name: "Palans électriques" },
    { id: "cranes", name: "Ponts roulants" },
    { id: "parts", name: "Pièces détachées" },
    { id: "accessories", name: "Accessoires" },
    { id: "tools", name: "Outils" }
  ];

  const mockProducts = [
    {
      id: 1,
      name: "Catalogue EXL Pont Roulant 2",
      brand: "exl",
      category: "cranes",
      price: 15899,
      image: "https://customer-assets.emergentagent.com/job_2637aafd-ad04-46aa-8ba4-c863793bcfd1/artifacts/3yxojrzv_Catalogue%20EXL%20Pont%20Roulant%202.png",
      description: "Palan électrique professionnel avec crochet intégré et chaînes de levage",
      specifications: ["Capacité: 2000kg", "Hauteur de levage: 6m", "Alimentation: 380V triphasé", "Certification CSA"]
    },
    {
      id: 2,
      name: "EXL Crochet de Levage Professionnel",
      brand: "exl",
      category: "parts",
      price: 299,
      image: "https://customer-assets.emergentagent.com/job_2637aafd-ad04-46aa-8ba4-c863793bcfd1/artifacts/qi549ufn_EXL%20Crochet%20de%20Levage%20Professionnel.png",
      description: "Crochet de levage professionnel avec verrouillage de sécurité",
      specifications: ["Charge max: 5T", "Acier forgé haute résistance", "Verrouillage automatique", "Certification CSA/ASME"]
    },
    {
      id: 3,
      name: "EXL Télécommande de Pont Roulant",
      brand: "exl",
      category: "accessories",
      price: 1299,
      image: "https://customer-assets.emergentagent.com/job_2637aafd-ad04-46aa-8ba4-c863793bcfd1/artifacts/bt88twmb_EXL%20T%C3%A9l%C3%A9commande%20de%20Pont%20Roulant.png",
      description: "Télécommande sans fil professionnelle avec bouton d'arrêt d'urgence",
      specifications: ["Portée: 100m", "Batterie longue durée", "Bouton d'arrêt d'urgence", "Étanche IP65"]
    },
    {
      id: 4,
      name: "EXL Potence Articulée Premium",
      brand: "exl",
      category: "cranes",
      price: 8999,
      image: "https://customer-assets.emergentagent.com/job_2637aafd-ad04-46aa-8ba4-c863793bcfd1/artifacts/m4d19zfh_EXL%20Potence%20Articul%C3%A9e%20Premium.png",
      description: "Potence articulée murale pour espaces restreints avec crochet de sécurité",
      specifications: ["Capacité: 1000kg", "Portée: 3m", "Rotation: 180°", "Montage mural"]
    },
    {
      id: 5,
      name: "EXL Rail Monorail Aluminium",
      brand: "exl",
      category: "accessories",
      price: 4599,
      image: "https://customer-assets.emergentagent.com/job_2637aafd-ad04-46aa-8ba4-c863793bcfd1/artifacts/qpzl5c87_EXL%20Rail%20Monorail%20Aluminium.png",
      description: "Système de rail monorail en aluminium avec chariots de suspension",
      specifications: ["Longueur: 6m", "Charge max: 2000kg", "Aluminium anodisé", "Chariots inclus"]
    }
  ];

  const filteredProducts = mockProducts.filter(product => {
    const matchesBrand = selectedBrand === "all" || product.brand === selectedBrand;
    const matchesCategory = selectedCategory === "all" || product.category === selectedCategory;
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesBrand && matchesCategory && matchesSearch;
  });

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-excel-steel to-excel-dark py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center text-white">
            <h1 className="text-4xl md:text-5xl font-bold mb-6 font-montserrat">
              Boutique en ligne
            </h1>
            <p className="text-xl max-w-3xl mx-auto leading-relaxed">
              Équipements de levage standards, pièces détachées et accessoires de nos marques partenaires
            </p>
          </div>
        </div>
      </section>

      {/* Brands Section */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-excel-dark mb-4 font-montserrat">
              Nos marques partenaires
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {brands.map((brand) => (
              <Card key={brand.id} className="text-center hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="h-20 flex items-center justify-center mb-4">
                    <img
                      src={brand.logo}
                      alt={brand.name}
                      className="max-h-16 w-auto"
                    />
                  </div>
                  <CardTitle className="text-excel-dark">{brand.name}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="mb-4">
                    {brand.description}
                  </CardDescription>
                  {brand.available ? (
                    <Badge className="bg-green-100 text-green-800">
                      Disponible en ligne
                    </Badge>
                  ) : (
                    <div className="space-y-2">
                      <Badge variant="outline" className="text-excel-steel">
                        Redirection externe
                      </Badge>
                      <div>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => window.open(brand.externalUrl, '_blank')}
                        >
                          <ExternalLink className="h-4 w-4 mr-2" />
                          Visiter le site
                        </Button>
                      </div>
                    </div>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Filters */}
          <div className="mb-8">
            <div className="flex flex-col md:flex-row gap-4 items-center">
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Rechercher un produit..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              
              <Select value={selectedBrand} onValueChange={setSelectedBrand}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Toutes les marques" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes les marques</SelectItem>
                  <SelectItem value="exl">EXL</SelectItem>
                  <SelectItem value="vulcan">VulcanHoist</SelectItem>
                  <SelectItem value="gorbel">Gorbel</SelectItem>
                </SelectContent>
              </Select>

              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="w-48">
                  <SelectValue placeholder="Toutes catégories" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes catégories</SelectItem>
                  {categories.map((category) => (
                    <SelectItem key={category.id} value={category.id}>
                      {category.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Products Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredProducts.map((product) => (
              <Card key={product.id} className="group hover:shadow-lg transition-all">
                <div className="h-48 overflow-hidden bg-gray-50 flex items-center justify-center">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-contain group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <Badge variant="outline" className="text-xs">
                      {brands.find(b => b.id === product.brand)?.name}
                    </Badge>
                    <div className="text-2xl font-bold text-excel-red">
                      ${product.price}
                    </div>
                  </div>
                  <CardTitle className="text-lg text-excel-dark">
                    {product.name}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="mb-4">
                    {product.description}
                  </CardDescription>
                  
                  <div className="space-y-2 mb-4">
                    {product.specifications.map((spec, index) => (
                      <div key={index} className="text-sm text-gray-600">
                        • {spec}
                      </div>
                    ))}
                  </div>

                  <Button className="w-full bg-excel-light-red hover:bg-excel-red text-white">
                    <ShoppingCart className="h-4 w-4 mr-2" />
                    Ajouter au panier
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          {filteredProducts.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg">Aucun produit trouvé avec ces critères.</p>
            </div>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default ShopPage;